import { throttle } from "es-toolkit";
import { useEffect, useRef, useState } from "react";

import {
  TOUCH_TARGET,
  TOUCH_TARGET_CLIENT_X,
  TOUCH_TARGET_CLIENT_Y,
  TOUCH_TARGET_END,
  TOUCH_TARGET_MOVE_OFF,
  TOUCH_TARGET_MOVE_ON,
  TOUCH_TARGET_START,
  TOUCH_TARGET_STOP_PROPAGATION,
} from "./constants.ts";

export type TouchPosition = {
  x: number;
  y: number;
};

export const useTouchPosition = (containerRef?: HTMLElement | SVGSVGElement, zoom = 1, offset = { x: 0, y: 0 }) => {
  const touchTargets = useRef<HTMLDivElement[]>([]);
  const [touchPosition, setTouchPosition] = useState<TouchPosition>();

  useEffect(() => {
    if (!containerRef) return;

    const containerCoordinates = containerRef.getBoundingClientRect();

    const getTouchTargets = ({ changedTouches }: TouchEvent): HTMLDivElement[] => {
      const clientX = changedTouches[0].clientX;
      const clientY = changedTouches[0].clientY;

      const elements = document.elementsFromPoint(clientX, clientY);

      return elements
        .filter((element) => element.getAttribute(TOUCH_TARGET))
        .map((element) => {
          element.setAttribute(TOUCH_TARGET_CLIENT_X, `${clientX}`);
          element.setAttribute(TOUCH_TARGET_CLIENT_Y, `${clientY}`);
          return element;
        }) as HTMLDivElement[];
    };

    const removeTouchTargetAttributes = (element: HTMLDivElement) => {
      element.removeAttribute(TOUCH_TARGET_START);
      element.removeAttribute(TOUCH_TARGET_MOVE_ON);
      element.removeAttribute(TOUCH_TARGET_MOVE_OFF);
      element.removeAttribute(TOUCH_TARGET_END);
      element.removeAttribute(TOUCH_TARGET_CLIENT_X);
      element.removeAttribute(TOUCH_TARGET_CLIENT_Y);
    };

    const updateTouchPosition = throttle(({ changedTouches }: TouchEvent) => {
      const clientX = changedTouches[0].clientX;
      const clientY = changedTouches[0].clientY;

      setTouchPosition({
        x: (clientX - containerCoordinates.x) / zoom + offset.x,
        y: (clientY - containerCoordinates.y) / zoom + offset.y,
      });
    }, 50);

    const touchStart = (event: TouchEvent) => {
      const targets = getTouchTargets(event);

      touchTargets.current = targets;

      for (const target of targets) {
        target.setAttribute(TOUCH_TARGET_START, "true");
        target.click();
        removeTouchTargetAttributes(target);

        const stopPropagation = target.getAttribute(TOUCH_TARGET_STOP_PROPAGATION) === "true";

        if (stopPropagation) {
          target.removeAttribute(TOUCH_TARGET_STOP_PROPAGATION);
          break;
        }
      }
    };

    const touchMove = (event: TouchEvent) => {
      updateTouchPosition(event);

      const targets = getTouchTargets(event);
      const existingTargets = touchTargets.current;

      for (const target of existingTargets) {
        if (!targets.includes(target)) {
          target.setAttribute(TOUCH_TARGET_MOVE_OFF, "true");
          target.click();
          removeTouchTargetAttributes(target);

          const stopPropagation = target.getAttribute(TOUCH_TARGET_STOP_PROPAGATION) === "true";

          if (stopPropagation) {
            target.removeAttribute(TOUCH_TARGET_STOP_PROPAGATION);
          }

          touchTargets.current?.filter((t) => t !== target);
        }
      }

      touchTargets.current = targets;

      for (const target of targets) {
        target.setAttribute(TOUCH_TARGET_MOVE_ON, "true");
        target.click();
        removeTouchTargetAttributes(target);

        const stopPropagation = target.getAttribute(TOUCH_TARGET_STOP_PROPAGATION) === "true";

        if (stopPropagation) {
          target.removeAttribute(TOUCH_TARGET_STOP_PROPAGATION);
          break;
        }
      }
    };

    const touchEnd = (event: TouchEvent) => {
      touchTargets.current = [];

      const targets = getTouchTargets(event);

      for (const target of targets) {
        target.setAttribute(TOUCH_TARGET_END, "true");
        target.click();
        removeTouchTargetAttributes(target);

        const stopPropagation = target.getAttribute(TOUCH_TARGET_STOP_PROPAGATION) === "true";

        if (stopPropagation) {
          target.removeAttribute(TOUCH_TARGET_STOP_PROPAGATION);
          break;
        }
      }
    };

    window.addEventListener("touchstart", touchStart, true);
    window.addEventListener("touchmove", touchMove, true);
    window.addEventListener("touchend", touchEnd, true);

    return () => {
      window.removeEventListener("touchstart", touchStart, true);
      window.removeEventListener("touchmove", touchMove, true);
      window.removeEventListener("touchend", touchEnd, true);
      updateTouchPosition.cancel();
    };
  }, [containerRef, offset, zoom]);

  return touchPosition;
};
